import React from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import BlockQuote, { IBlockQuoteProps } from '../BlockQuote';
import StatBarStat, { IStatBarStatProps } from '../Stat';

export interface ICaseStudyPromoProps extends IBaseElementProps {
	style?: 'Default' | 'Small';
	heading?: string;
	headingSize?: 'H2' | 'H3';
	subHeading?: string;
	/**
	 * @control textarea
	 */
	description?: string;
	stats: IStatBarStatProps[];
	statsSize?: 'Default' | 'Small';
	cta?: ILink;
	image?: IImage;
	imageSize?: 'Default' | 'Small';
	blockQuote?: IBlockQuoteProps;
	/**
	 * @noUI
	 */
	inverted?: boolean;
}

/**
 * Calian Case Study Promo
 * @icon format-gallery
 * @block
 */
const CaseStudyPromo: React.FC<ICaseStudyPromoProps> = ({
	id,
	className,
	classNames = [],
	style,
	heading,
	headingSize,
	subHeading,
	description,
	stats,
	statsSize,
	cta,
	image,
	imageSize,
	blockQuote,
	inverted = false,
}) => {
	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-case-study-promo px-3',
					inverted && 'inverted',
					...classNames,
				)
			}
		>
			<div
				className={cn(
					'calian-case-study-promo__container container-fluid',
					!style || (style !== 'Small' && 'py-md-12'),
					style === 'Small' && 'py-md-9',
				)}
			>
				<div className="calian-case-study-promo__wrapper d-flex gap-5 flex-column flex-md-row">
					<div className="calian-case-study-promo__content-left order-2 order-md-1">
						{subHeading && (
							<div className="calian-case-study-promo__subheading text-white">
								{subHeading}
							</div>
						)}
						{heading &&
							(headingSize && headingSize === 'H3' ? (
								<h3 className="calian-case-study-promo__heading text-white mb-3">
									{heading}
								</h3>
							) : (
								<h2 className="calian-case-study-promo__heading text-white mb-3">
									{heading}
								</h2>
							))}
						{stats && stats.length > 0 && (
							<div
								className={cn(
									'calian-case-study-promo__stats d-flex gap-0 gap-lg-7 flex-column flex-lg-row',
									statsSize === 'Small' &&
										'calian-case-study-promo__stats--small',
								)}
							>
								{stats.map((stat, index) => (
									<div key={index}>
										<StatBarStat {...stat} />
									</div>
								))}
							</div>
						)}
						{description && (
							<div
								dangerouslySetInnerHTML={{
									__html: description,
								}}
								className="calian-case-study-promo__description mt-5"
							/>
						)}
						{(cta?.label || cta?.href) && (
							<div className="calian-case-study-promo__cta mt-5 mt-md-6">
								<Button
									className={cn(
										`btn btn-${
											inverted ? 'secondary' : 'light'
										}`,
									)}
									{...cta}
								/>
							</div>
						)}
					</div>
					<div className="calian-case-study-promo__content-right order-1 order-md-2 ms-lg-auto">
						{image && (
							<Image
								className={cn(
									'calian-case-study-promo__image d-flex mx-auto ms-md-0 ms-lg-auto me-md-0 rounded-5',
									imageSize === 'Small' &&
										'calian-case-study-promo__image--small',
								)}
								{...image}
							/>
						)}
						{blockQuote?.quote && (
							<div className="calian-case-study-promo__block-quote mt-md-7 ms-0 ms-md-3 ms-xl-0 mt-5 mt-md-0">
								<BlockQuote
									className="color-white"
									{...blockQuote}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
export default CaseStudyPromo;
